<template>
  <BaseDialog
    contentClass="tender-details"
    ref="dialog"
    sidePanel
    showActions
    @closeModal="closeDialog"
    @opened="onDialogOpened"
    :onClose="closeDialog"
  >
    <template slot="title">
      <span
        class="tender-details-dialog__title"
        v-if="tender"
        v-tooltip="{ content: tender.title, placement: 'bottom' }"
      >
        {{ tender.title }}
      </span>
    </template>
    <template slot="content">
      <OpportunitySourceTender
        v-if="isOpen && tender"
        :tenderId="tender.id"
        :competitorId="competitorId"
        :showTitle="false"
        hide-view-tender
        @tender-updated="hasTenderChanged = true"
      />
    </template>
    <template slot="actions">
      <BaseButton isFullWidth hasIconRight @click.native="openTenderDetails">
        <span>{{ $t('page.opportunity-details.view-tender') }}</span>
        <BaseIcon name="arrow-right" />
      </BaseButton>
    </template>
  </BaseDialog>
</template>

<script>
import { bus } from '@/main';

import BaseButton from '@/components/base/BaseButton.vue';
import BaseDialog from '@/components/base/BaseDialog.vue';
import BaseIcon from '@/components/base/BaseIcon';
import OpportunitySourceTender from '@/components/page-opportunity-details/OpportunitySourceTender.vue';

export default {
  name: 'TenderDetailsDialog',
  components: {
    BaseButton,
    BaseDialog,
    BaseIcon,
    OpportunitySourceTender,
  },
  props: {
    tender: {
      type: Object,
    },
    competitorId: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      hasTenderChanged: false,
      isOpen: false,
      triggerElement: null,
    };
  },
  computed: {},
  methods: {
    openDialog() {
      this.hasTenderChanged = false;
      // Remember which element opened the dialog so we can restore focus on close.
      this.triggerElement = document.activeElement;
      this.isOpen = true;
      this.$refs.dialog.openModal();
    },
    onDialogOpened() {
      // Move focus into the dialog so keyboard users can immediately act on it.
      // The close button is a safe default that doesn't preempt the user's intent.
      this.$nextTick(() => {
        const closeBtn = this.$refs.dialog && this.$refs.dialog.$refs.closeButton;
        if (closeBtn && typeof closeBtn.focus === 'function') {
          closeBtn.focus();
        }
      });
    },
    closeDialog() {
      bus.$emit('last-viewed-tender-id', this.tender.id);
      if (this.hasTenderChanged) {
        this.$emit('on-tender-changed');
      }
      this.isOpen = false;
      this.$refs.dialog.isModalOpen = false;
      // Return focus to the row/control that opened the dialog.
      const trigger = this.triggerElement;
      this.triggerElement = null;
      if (trigger && typeof trigger.focus === 'function' && document.body.contains(trigger)) {
        this.$nextTick(() => trigger.focus());
      }
    },
    openTenderDetails() {
      if (this.tender) {
        const routeData = this.$router.resolve({
          name: 'tender-details',
          params: { id: this.tender.id },
        });
        window.open(routeData.href, '_blank', 'noopener,noreferrer');
      }
    },
  },
};
</script>

<style lang="scss">
.base-dialog__tender-details {
  min-width: 360px;
  max-width: 560px;
  width: 40vw;

  .base-dialog__header {
    .header__title {
      flex: 1 1 auto;
      min-width: 0;
      width: auto;
      overflow: hidden;
    }

    .close-icon__container {
      flex-shrink: 0;
    }
  }

  .tender-details-dialog__title {
    display: block;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.4;
    color: $color-normal;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .base-dialog__content {
    padding-top: 0px !important;
    background: $color-white !important;

    .opportunity-source-tender__base-card {
      padding: 0 16px 16px;

      // Pin the first card (metadata row) to the top of the scroll container.
      // It carries Statut · # Référence · Date de fermeture · Source — context
      // we don't want users to lose when they scroll through bidders/documents.
      > .opportunity-source-tender__card:first-of-type {
        position: sticky;
        top: 0;
        z-index: 2;
        background: $color-white;
      }
    }
  }

  .base-dialog__actions {
    padding: 16px;
    border-top: 1px solid $color-border;
    background: $color-white;
    box-shadow: 0 -4px 12px rgba(77, 92, 116, 0.06);

    .base-button {
      width: 100%;
    }
  }
}
</style>
